import { addDays } from 'date-fns'
import type { TFunction } from 'i18next'

import { DEFAULT_INVOICE_PREFIX } from '@features/invoices/constants/defaultInvoicePrefix'
import { invoiceLocaleToLanguage } from '@features/invoices/constants/invoiceLocaleToLanguage'
import { InvoiceDiscountType } from '@features/invoices/enums/invoiceDiscountType'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import type { ZervantLocale } from '@features/invoices/enums/zervantLocale'
import type { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import type { InvoiceLineColumn } from '@features/invoices/types/invoiceLineColumn'
import type { InvoicePayload } from '@features/invoices/types/invoicePayload'
import { getInvoicePayload } from '@features/invoices/utils/invoicePayload/getInvoicePayload'
import type { ProductType } from '@features/products/enums/productType'
import type { Organization } from '@modules-deprecated/app/organization/types/organization'

import { demoInvoicingSettingsInvoiceLine } from '../constants/demoInvoicingSettingsInvoiceLine'
import { demoInvoicingSettingsInvoiceReceiver } from '../constants/demoInvoicingSettingsInvoiceReceiver'
import type { ZervantTemplateSettings } from '../types/templates/zervantTemplateSettings'
import type { ZervantSettings } from '../types/zervantSettings'

interface InvoicingSettingsPreviewValues {
  currency: string
  footerNote: string
  language: ZervantLocale
  message: string
  paymentTerm: number
  productType: ProductType
  vatRateId?: string
  vatRatePercent?: number
}

interface GetInvoicingSettingsPreviewPayloadOptions {
  customTemplateSettings: ZervantTemplateSettings
  documentDate: Date
  organization?: Organization
  previewValues: InvoicingSettingsPreviewValues
  t: TFunction
  visibleColumns: InvoiceLineColumn[]
  zervantSettings?: ZervantSettings
}

export const getInvoicingSettingsPreviewPayload = (
  options: GetInvoicingSettingsPreviewPayloadOptions,
): InvoicePayload => {
  const { customTemplateSettings, documentDate, organization, previewValues, t, visibleColumns, zervantSettings } =
    options
  const invoiceValues: InvoiceForm = {
    currency: previewValues.currency,
    dateOfSaleOrService: documentDate,
    discount: 0,
    discountType: InvoiceDiscountType.Percentage,
    displayedPaymentMethods: { bank: true, offlinePayment: true, paypal: true },
    dueDate: addDays(documentDate, previewValues.paymentTerm),
    footerNote: previewValues.footerNote,
    invoiceLines: [
      {
        ...demoInvoicingSettingsInvoiceLine,
        date: documentDate,
        productName: t('external_invoices.preview.example.product_name'),
        productType: previewValues.productType,
        vatRateId: previewValues.vatRateId,
        vatRatePercent: previewValues.vatRatePercent,
      },
    ],
    invoiceNumber: 12345,
    invoiceNumberPrefix: DEFAULT_INVOICE_PREFIX,
    issueDate: documentDate,
    language: previewValues.language,
    message: previewValues.message,
    paymentTerm: previewValues.paymentTerm,
    receiver: {
      ...demoInvoicingSettingsInvoiceReceiver,
      invoicingLanguage: invoiceLocaleToLanguage[previewValues.language],
      name: t('external_invoices.preview.example.receiver_name'),
    },
  }

  return getInvoicePayload({
    customTemplateSettings,
    documentType: InvoiceDocumentType.Invoice,
    invoiceValues,
    organization,
    t,
    visibleColumns,
    zervantSettings,
  })
}
